import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

import RichTextEditor from "../../../shared/RichTextEditor";
import { Fetch } from "../../../shared/Fetch";


interface ICompanyTemplate {
    companyId: number;
    companyName: string;
    templateCount: number;
}

interface IEmailTemplate {
    template_Id?: number;
    companyId?: number;
    companyName?: string;
    template_Name?: string;
    template_Subject: string;
    template_Body: string;
}

interface IProps {
    toast?: any;
}


export const EmailTemplateSetting = (props: IProps) => {
    const [companies, setCompanies] = useState<ICompanyTemplate[]>([]);
    const [companyLoading, setCompanyLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<ICompanyTemplate | null>(null);
    const [templates, setTemplates] = useState<IEmailTemplate[]>([]);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [templateDialog, setTemplateDialog] = useState(false);
    const [viewDialog, setViewDialog] = useState(false);
    const [formDialog, setFormDialog] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<IEmailTemplate | null>(null);
    const [emailSubject, setEmailSubject] = useState("");
    const [emailTemplate, setEmailTemplate] = useState("");
    const [formLoading, setFormLoading] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");
    const [templateFilter, setTemplateFilter] = useState("");

    const loadCompanies = async () => {
        setCompanyLoading(true);

        try {
            const response = await Fetch("lead-capture/getCompanyEmailTemplatesCount", "GET", null, null, props.toast);

            if (response) {
                const data = await response.json();
                console.log("Company API Response:", data);
                const mappedData: ICompanyTemplate[] = Array.isArray(data)
                    ? data.map((item: any) => ({
                        companyId: item.company_ID,
                        companyName: item.company_Name,
                        templateCount: item.count ?? 0,
                    }))
                    : [];

                setCompanies(mappedData);
            }
        } catch (error) {
            console.error(
                "Error loading company email template count:",
                error
            );
        } finally {
            setCompanyLoading(false);
        }
    };

    const loadCompanyTemplates = async (companyId: number) => {
        setTemplateLoading(true);
        try {
            const response = await Fetch("lead-capture/getCompanyEmailTemplates", "GET", null, `companyID=${companyId}`, props.toast);
            if (response) {
                const data = await response.json();

                const templateData: IEmailTemplate[] =
                    Array.isArray(data)
                        ? data.map((item: any) => ({
                            template_Id: item.ID ?? item.id ?? item.template_Id,
                            companyId: item.Company_ID ?? item.companyID ?? item.companyId,
                            companyName: item.Company_Name ?? item.companyName,
                            template_Subject: item.Email_Subject ?? item.email_Subject ?? item.template_Subject ?? "",
                            template_Body: item.Email_Template ?? item.email_Template ?? item.template_Body ?? "",
                        }))
                        : [];

                setTemplates(templateData);
            }

        } catch (error) {

            setTemplates([]);

        } finally {

            setTemplateLoading(false);
        }
    };

    useEffect(() => {
        loadCompanies();
    }, []);


    const handleTemplateCountClick = async (company: ICompanyTemplate) => {
        setSelectedCompany(company);
        setTemplateFilter("");
        await loadCompanyTemplates(company.companyId);
        setTemplateDialog(true);
    };

    const handleViewTemplate = (template: IEmailTemplate) => {
        setSelectedTemplate(template);
        setViewDialog(true);
    };


    const handleAddTemplate = () => {
        if (!selectedCompany) {
            return;
        }

        setSelectedTemplate(null);
        setEmailSubject("");
        setEmailTemplate("");
        setFormDialog(true);
    };


    const handleEditTemplate = (template: IEmailTemplate) => {
        setSelectedTemplate(template);
        setEmailSubject(template.template_Subject || "");
        setEmailTemplate(template.template_Body || "");
        setFormDialog(true);
    };

    const handleSaveTemplate = async () => {
        if (!selectedCompany) {
            return;
        }

        if (!emailSubject.trim()) {
            props.toast?.current?.show({
                severity: "error",
                detail: "Email Subject is required.",
                life: 3000,
            });
            return;
        }

        if (!emailTemplate.trim()) {
            props.toast?.current?.show({
                severity: "error",
                detail: "Email Template is required.",
                life: 3000,
            });
            return;
        }

        setFormLoading(true);

        try {
            let response: Response | undefined;
            if (!selectedTemplate?.template_Id) {
                const body = {
                    Company_ID: selectedCompany.companyId,
                    Email_Subject: emailSubject,
                    Email_Template: emailTemplate,
                };

                response = await Fetch("lead-capture/addCompanyEmailTemplate", "POST", body, null, props.toast);
            }

            else {
                const body = {
                    ID: selectedTemplate.template_Id,
                    Company_ID: selectedCompany.companyId,
                    Email_Subject: emailSubject,
                    Email_Template: emailTemplate,
                };

                response = await Fetch("lead-capture/updateCompanyEmailTemplate", "PUT", body, null, props.toast);
            }

            if (!response) {
                return;
            }

            props.toast?.current?.show({
                severity: "success",
                detail: selectedTemplate?.template_Id ? "Email template updated successfully." : "Email template added successfully.",
                life: 3000,
            });

            setFormDialog(false);
            setSelectedTemplate(null);
            setEmailSubject("");
            setEmailTemplate("");
            await loadCompanyTemplates(selectedCompany.companyId);
            await loadCompanies();
        } catch (error) {
            console.error("Error saving email template:", error);
        } finally {
            setFormLoading(false);
        }
    };

    const closeTemplateDialog = () => {
        setTemplateDialog(false);
        setSelectedCompany(null);
        setTemplates([]);
        setTemplateFilter("");
    };


    const closeViewDialog = () => {
        setViewDialog(false);
        setSelectedTemplate(null);
    };


    const closeFormDialog = () => {
        setFormDialog(false);
        setSelectedTemplate(null);
        setEmailSubject("");
        setEmailTemplate("");
    };


    const getPlainText = (html: string) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        return (
            tempDiv.textContent ||
            tempDiv.innerText ||
            ""
        );
    };


    const renderTemplateBody = (rowData: IEmailTemplate) => {

        const plainText = getPlainText(rowData.template_Body || "").trim();
        if (plainText.length > 80) {
            return (
                `${plainText.substring(0, 80)}...`
            );
        }

        return plainText;
    };


    const templateCountBody = (rowData: ICompanyTemplate) => {
        return (
            <Button
                label={String(rowData.templateCount)}
                className="p-button-link p-0"
                style={{ fontWeight: 600, minWidth: "40px", }}
                onClick={() => handleTemplateCountClick(rowData)}
            />
        );
    };


    const actionBodyTemplate = (rowData: IEmailTemplate) => {
        return (
            <div className="flex align-items-center gap-1">
                <Button icon="pi pi-eye" className="p-button-rounded p-button-text" tooltip="View" tooltipOptions={{ position: "top", }} onClick={() => handleViewTemplate(rowData)} />
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" tooltip="Edit" tooltipOptions={{ position: "top", }} onClick={() => handleEditTemplate(rowData)} />

            </div>
        );
    };


    const filteredCompanies =
        companies.filter((company) =>
            company.companyName.toLowerCase().includes(globalFilter.toLowerCase())
        );

    const filteredTemplates =
        templates.filter((template) => {
            const subject = (template.template_Subject || "").toLowerCase();
            const body = getPlainText(template.template_Body || "").toLowerCase();
            const search = templateFilter.toLowerCase();
            return (
                subject.includes(search) ||
                body.includes(search)
            );
        });

    const companyTableHeader = (
        <div className="flex justify-content-between align-items-center">
            <h5 className="m-0"> Email Template Settings</h5>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />

                <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search Company" />

            </span>
        </div>
    );



    const templateTableHeader = (
        <div className="flex justify-content-between align-items-center">
            <h5 className="m-0"> {selectedCompany?.companyName} {" - Email Templates"}</h5>
            <div className="flex align-items-center gap-2">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={templateFilter}
                        onChange={(e) => setTemplateFilter(e.target.value)}
                        placeholder="Search Template"
                    />
                </span>
                <Button label="Add" icon="pi pi-plus" onClick={handleAddTemplate} />
            </div>

        </div>
    );


    return (
        <div className="card">

            <DataTable value={filteredCompanies} header={companyTableHeader} paginator rows={10} size="small" loading={companyLoading} emptyMessage="No Company Found." stripedRows responsiveLayout="scroll"
                style={{ border: "1px solid rgba(105,105,105,0.17)", }}
            >

                <Column field="companyName" header="Company Name" sortable />

                <Column field="templateCount" header="Email Template Count" body={templateCountBody} sortable style={{ width: "220px", textAlign: "center", }} />

            </DataTable>

            <Dialog
                visible={templateDialog}
                onHide={closeTemplateDialog}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-envelope" />
                        <span>{selectedCompany?.companyName}</span>
                    </div>
                }
                modal
                style={{ width: "80vw", }}
                breakpoints={{ "960px": "90vw", "641px": "95vw", }}
            >

                <DataTable value={filteredTemplates} header={templateTableHeader} paginator rows={5} size="small" loading={templateLoading} emptyMessage="No Email Template Found." stripedRows responsiveLayout="scroll"
                    style={{ border: "1px solid rgba(105,105,105,0.17)", borderTopWidth: "0", }}
                >

                    <Column field="template_Subject" header="Template Subject" sortable />

                    <Column field="template_Body" header="Template Body" body={renderTemplateBody} />

                    <Column header="Action" body={actionBodyTemplate} style={{ width: "120px", textAlign: "center", }} />

                </DataTable>

            </Dialog>

            <Dialog visible={viewDialog} onHide={closeViewDialog} header="View Email Template" modal style={{ width: "70vw", }} breakpoints={{ "960px": "85vw", "641px": "95vw", }}>
                {selectedTemplate && (
                    <div className="p-fluid">
                        <div className="field">
                            <label htmlFor="viewSubject" className="font-semibold">
                                Email Subject
                            </label>

                            <InputText
                                id="viewSubject"
                                value={selectedTemplate.template_Subject}
                                readOnly
                            />

                        </div>


                        <div className="field">
                            <label className="font-semibold mb-2 block">
                                Email Template
                            </label>

                            <RichTextEditor
                                value={selectedTemplate.template_Body || ""}
                                onChange={() => { }}
                                height={400}
                                readonly={true}
                            />
                        </div>

                        <div className="flex justify-content-end mt-3">
                            <Button
                                label="CLOSE"
                                icon="pi pi-times"
                                className="p-button-outlined"
                                style={{ width: "100px", }}
                                onClick={closeViewDialog}
                            />
                        </div>
                    </div>
                )}

            </Dialog>


            <Dialog
                visible={formDialog}
                onHide={closeFormDialog}
                header={selectedTemplate ? "Edit Email Template" : "Add Email Template"}
                modal
                style={{ width: "70vw", }}
                breakpoints={{ "960px": "85vw", "641px": "95vw", }}
            >

                <div className="p-fluid">

                    <div className="field">

                        <label htmlFor="emailSubject" className="font-semibold">
                            Email Subject
                            <span className="text-red-500"> {" "}*</span>
                        </label>

                        <InputText
                            id="emailSubject"
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            placeholder="Enter email subject"
                        />

                    </div>

                    <div className="field">
                        <label className="font-semibold mb-2 block">
                            Email Template
                            <span className="text-red-500">
                                {" "}*
                            </span>
                        </label>

                        <RichTextEditor
                            value={emailTemplate}
                            onChange={setEmailTemplate}
                            height={400}
                            readonly={false}
                            placeholder="Enter email template..."
                        />

                    </div>


                    <div className="flex justify-content-end gap-2 mt-4">

                        <Button
                            label="CLOSE"
                            icon="pi pi-times"
                            type="button"
                            className="p-button-outlined"
                            style={{ width: "100px", }}
                            onClick={closeFormDialog}
                        />

                        <Button
                            label={selectedTemplate ? "UPDATE" : "SAVE"}
                            icon={selectedTemplate ? "pi pi-check" : "pi pi-save"}
                            loading={formLoading}
                            style={{ width: "100px", }}
                            onClick={handleSaveTemplate}
                        />

                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default EmailTemplateSetting;